<nz-card>
  <!--
    Two columns: who the customer is, and what they owe or hold.

    Before, all nine things in this card stacked full-width in one column, and
    the largest object on it was an `nz-input` holding a name that cannot be
    edited here — editing has its own branch below. It occupied most of the
    width, promised an interaction it did not offer, and pushed everything else
    into a queue.

    The four figures carried no visible label; only a tooltip said which was
    which. That is why all four had to be fully saturated — colour was doing a
    label's job, and four saturated chips in a row is what made the card shout.
    Once labelled, colour is free to mean something instead: owed, held, or
    neither. Two hues where there were four, and they now say more.
  -->
  <div class="uc">
    <div class="uc__who">
      @if (userUpdating()) {
        <nz-input-group
          [nzSuffix]="prefixTemplateUserSelect"
          >
          <input
            type="text"
            nz-input
            [ngModel]="selectedUser()?.fullName"
            [ngModelOptions]="{ standalone: true }"
            placeholder="{{ 'calendar.form.user' | translate }}"
            />
        </nz-input-group>
        <ng-template #prefixTemplateUserSelect>
          <button nz-button nzType="primary"
            nz-popconfirm
            nzPopconfirmTitle="დარწმუნებული ხართ რომ გინდათ მომხმარებლის შეცვლა?"
            (nzOnConfirm)="saveUser()"
            >
            <i nz-icon nzType="save" nzTheme="outline"></i>
            შენახვა
          </button>
          <button nz-button nzType="default" type="button"
            style="margin-left: 8px"
            (click)="cancelUserChange()"
            [nz-tooltip]="'buttons.cancel' | translate"
            >
            <i nz-icon nzType="close" nzTheme="outline"></i>
            {{ 'buttons.cancel' | translate }}
          </button>
        </ng-template>
      } @else {
        <div class="uc__ident" [class.uc__ident--picker]="scheduling()">
          @if (scheduling()) {
            <!-- Field and its verified mark travel together; the actions stay
                 at the far edge. Loose in the row, space-between spread all
                 three evenly and the tick drifted into the middle of nowhere. -->
            <div class="uc__search-wrap">
            <!-- beaut-545. A field, not an icon — and it stays a field after the
                 pick. On this screen the customer is still being decided; the
                 operator who chose the wrong one should correct it where they
                 chose it, not hunt for a swap icon. On the booked visit the
                 name is text, as it always was: there a change is a saved
                 change, and it goes through its own confirm. -->
            <beautyapp-select-search
              class="uc__search"
              data-test="uc-customer-search"
              [ngModel]="selectedUser()?.id"
              [ngModelOptions]="{ standalone: true }"
              [data]="customerOptions()"
              (onSearch)="visitAddFacade.searchCustomer($event)"
              (selectedOption)="pickCustomer($event)"
              placeholder="{{ 'calendar.form.user' | translate }}"
              prefixIcon="user"
              valueMember="id"
              displayMember="fullName"
              size="large"
            ></beautyapp-select-search>
            @if (selectedUser()?.id && user()?.isVerified) {
                <span
                  class="uc__verified"
                  nz-icon
                  nzType="check-circle"
                  nzTheme="twotone"
                  nzTwotoneColor="#52c41a"
                  [nz-tooltip]="'users.form.verified' | translate"
                ></span>
              }
            </div>
          } @else if (selectedUser()?.id) {
            <!-- The name is text, not a field. It was never editable here. -->
            <span class="uc__name" [title]="selectedUser()?.fullName">
              @if (user()?.isVerified) {
                <span
                  nz-icon
                  nzType="check-circle"
                  nzTheme="twotone"
                  nzTwotoneColor="#52c41a"
                  [nz-tooltip]="'users.form.verified' | translate"
                ></span>
              } @else {
                <span nz-icon nzType="user" class="uc__name-icon"></span>
              }
              {{ selectedUser()?.fullName }}
            </span>
          } @else {
            <span class="uc__name uc__name--empty">
              {{ 'calendar.form.user' | translate }}
            </span>
          }

          <nz-space class="uc__actions">
            @if (selectedUser()?.id) {
              <button
                *nzSpaceItem
                (click)="viewUser.emit(selectedUser())"
                type="button"
                nz-button
                nzType="text"
                nzShape="circle"
                [nz-tooltip]="'users.form.selectUser' | translate"
                >
                <i nz-icon nzType="eye" nzTheme="outline"></i>
              </button>
            }
            <!-- beaut-298: the change-customer icon must be available whenever a
                 customer is selected. A 2025-08-27 "formatting" commit (8a86abb3)
                 silently gated it behind visit()?.status === PENDING/IN_PROGRESS,
                 and 369e93a0 added the visit() null-check — together they hid the
                 icon during scheduling and any flow where the facade visit isn't
                 loaded. Restore the original always-available behavior. -->
            <!-- beaut-358: but a COMPLETED (paid) visit rejects a customer change
                 server-side ("Visit is already completed. User cannot be updated."),
                 so hide it only when the loaded visit is COMPLETED. When no visit is
                 loaded (scheduling), status is undefined → still shown, keeping
                 beaut-298 intact. Mirrors the backend guard on visit.status. -->
            <!-- beaut-545. Next to the search field: the customer may not be in
                 the base yet. The search itself is the field, not an icon. -->
            @if (scheduling() && !selectedUser()?.id) {
              <button
                *nzSpaceItem
                (click)="addUser.emit()"
                type="button"
                nz-button
                nzType="text"
                nzShape="circle"
                data-test="uc-add-user"
                [nz-tooltip]="'calendar.createUser' | translate"
                >
                <i nz-icon nzType="user-add" nzTheme="outline"></i>
              </button>
            }
            <!-- beaut-545. Not while scheduling: the search field above IS the
                 way to change the customer there, and two controls for one
                 choice is one too many. -->
            @if (!scheduling() && selectedUser()?.id && visitAddFacade.visit()?.status !== 'COMPLETED') {
              <button
                *nzSpaceItem
                (click)="selectUser()"
                type="button"
                nz-button
                nzType="text"
                nzShape="circle"
                [nz-tooltip]="'users.form.selectUser' | translate"
                >
                <i nz-icon nzType="swap" nzTheme="outline"></i>
              </button>
            }
            <!-- beaut-545. Nothing to verify before a customer is chosen: on the
                 planning page this card opens empty, and !isVerified is true of
                 nobody just as readily as of an unverified person. -->
            @if (selectedUser()?.id && !user()?.isVerified) {
              <button
                *nzSpaceItem
                (click)="verifyUser.emit(selectedUser())"
                type="button"
                nz-button
                nzType="text"
                nzShape="circle"
                class="uc__verify"
                >
                <svg
                  viewBox="0 0 1024 1024" version="1.1"
                  xmlns="http://www.w3.org/2000/svg" width="18" height="18" aria-hidden="true">
                  <path
                    d="M426.666667 725.333333 256 554.666667 316.16 494.506667 426.666667 604.586667 707.84 323.413333 768 384M512 42.666667 128 213.333333 128 469.333333C128 706.133333 291.84 927.573333 512 981.333333 732.16 927.573333 896 706.133333 896 469.333333L896 213.333333 512 42.666667Z"
                    fill="currentColor"></path>
                </svg>
              </button>
            }
          </nz-space>
        </div>

        <!--
          `birthDate` reaches here as `YYYY-MM-DD` since users.birthDate became a
          `date` column (migration 1788200000000). Before that it arrived as an
          instant the process had already resolved in its own timezone, which the
          badge refuses rather than guess from.
        -->
        <beautyapp-birthday-badge
          [birthDate]="selectedUser()?.birthDate"
        ></beautyapp-birthday-badge>

      }

      <!--
        In the "who" column, under the name, rather than full width below both.
        The ledger is four short rows and the name is one line, so this column
        ran out of content first and the card carried an empty band across its
        middle. The comment fills it, and sits beside the person it is about.

        Outside the edit/display branch on purpose, so it stays visible while a
        name is being changed — that was true when it was full width and is the
        one property of its old position worth keeping.
      -->
      @if (latestComment()?.text) {
        <div class="uc__note">
          <i nz-icon nzType="comment" nzTheme="outline" class="uc__note-icon"></i>
          <div>
            <div class="uc__note-label">
              @if (latestComment()?.isPinned) {
                <i nz-icon nzType="pushpin" nzTheme="fill" class="uc__note-pin"></i>
              }
              ბოლო კომენტარი
            </div>
            <div class="uc__note-text">{{ latestComment()?.text }}</div>
          </div>
        </div>
      }
    </div>

    <!--
      A small ledger, not four chips. Labels make the figures self-describing,
      right alignment and tabular figures let them be compared down the column.

      Colour marks meaning, not category: red where the customer owes, green
      where they hold something. A zero stays ink — colouring a zero draws the
      eye to nothing, which is how four coloured chips became noise in the
      first place. Minutes are neither owed nor held and stay ink always.
    -->
    <dl class="uc__ledger">
      <div class="uc__row">
        <dt>{{ 'calendar.form.debt' | translate }}</dt>
        <dd [class.uc__owed]="(selectedUser()?.debt || 0) !== 0">
          {{ (selectedUser()?.debt || 0) | number: '1.2-2' }} {{ 'calendar.gel' | translate }}
        </dd>
      </div>
      <div class="uc__row">
        <dt>{{ 'calendar.form.balance' | translate }}</dt>
        <dd [class.uc__credit]="(selectedUser()?.balance || 0) > 0">
          {{ (selectedUser()?.balance || 0) | number: '1.2-2' }} {{ 'calendar.gel' | translate }}
        </dd>
      </div>
      <div class="uc__row">
        <dt>{{ 'calendar.form.minutes' | translate }}</dt>
        <dd>{{ selectedUser()?.minutes || 0 }} {{ 'calendar.minute' | translate }}</dd>
      </div>
      <div class="uc__row">
        <dt>{{ 'users.column.cashback' | translate }}</dt>
        <dd [class.uc__credit]="(selectedUser()?.cashback || 0) > 0">
          {{ (selectedUser()?.cashback || 0) | number: '1.2-2' }} {{ 'calendar.gel' | translate }}
        </dd>
      </div>
      <!-- As on the customer profile: own / gifted, and absent rather than
           "0 / 0" when the customer holds none. A count, not money, so ink. -->
      @if (packageCounts().own + packageCounts().gift > 0) {
        <div class="uc__row" data-test="uc-packages">
          <dt>{{ 'users.servicePackages' | translate }}</dt>
          <dd class="uc__packages">
            <span nz-icon nzType="appstore" nz-tooltip [nzTooltipTitle]="'users.ownPackages' | translate"></span>
            <span data-test="uc-own-packages">{{ packageCounts().own }}</span>
            <span class="uc__sep">/</span>
            <span nz-icon nzType="gift" nz-tooltip [nzTooltipTitle]="'users.giftPackages' | translate"></span>
            <span data-test="uc-gift-packages">{{ packageCounts().gift }}</span>
          </dd>
        </div>
      }
    </dl>
  </div>


  <div class="uc__settings">
    <!--
      Checkboxes, not switches. These are two options on the visit in front of
      you, and a pair of filled switches carried more weight than a settings row
      should — they read as the most important thing on the card.

      `[ngModel]` shows the signal; `(ngModelChange)` hands back the new value.
      NOT `(click)` on the label (beaut-605): ng-zorro stops the click on its
      inner input — the square people actually click — so a host `(click)`
      never ran while the checkbox still drew itself ticked. Clicking the text
      worked, which made it look intermittent. `ngModelChange` fires once per
      toggle wherever the click lands.
      `nzDisabled` during the save replaces the switch's spinner: quieter, and
      it also stops a second click landing mid-request.
    -->
    <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
    <label
      nz-checkbox
      class="uc__check"
      [ngModel]="smsNotification()"
      [ngModelOptions]="{ standalone: true }"
      [nzDisabled]="loading()"
      (ngModelChange)="clickSwitch($event)"
    >{{ 'addVisit.form.sendNotification' | translate }}</label>

    <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
    <label
      nz-checkbox
      class="uc__check"
      [ngModel]="userRequest()"
      [ngModelOptions]="{ standalone: true }"
      [nzDisabled]="loadingUserRequest()"
      (ngModelChange)="clickUserRequestSwitch($event)"
    >{{ 'calendar.form.userRequest' | translate }}</label>

    @if (selectedUser()?.id) {
      <div class="uc__setting">
        <span class="uc__setting-label">{{ 'users.form.smsLanguage' | translate }}:</span>
        @if (!smsLangOpen()) {
          <!--
            A value you can click, not a control. The bordered pill with its
            globe was the only outlined object left on the row and pulled far
            more attention than a language code deserves.

            Empty reads "branch default", not "not set" and not "KA".
            `resolveEffectiveLanguage` (libs/domain/src/sms) is:

              if (!advancedSmsEnabled) return KA;
              return userLanguage ?? branchDefaultLanguage ?? KA;

            so an unset customer language resolves to the BRANCH's default,
            which can be ru or en — printing KA here would be wrong for exactly
            those branches. "Not set" was also wrong in the other direction: a
            message does go out, in a language the branch already chose.
          -->
          <button
            type="button"
            (click)="smsLangOpen.set(true)"
            [nz-tooltip]="'users.form.smsLanguageHint' | translate"
            class="uc__lang"
          >{{ selectedUser()?.smsLanguage ? (selectedUser()?.smsLanguage | uppercase) : ('users.form.smsLanguageDefault' | translate) }}</button>
        } @else {
          <div class="uc__lang-group">
            @for (opt of smsLangOptions; track opt) {
              <button
                type="button"
                [disabled]="smsLangSaving()"
                (click)="setSmsLanguage(opt)"
                class="uc__lang-opt"
                [class.uc__lang-opt--on]="selectedUser()?.smsLanguage === opt"
              >{{ opt | uppercase }}</button>
            }
          </div>
        }
      </div>
    }
  </div>
</nz-card>
